<script setup>
import {
    onMounted, ref, shallowRef, watch,
} from "vue";
import { useRoute } from "vue-router";
import { changeDaySelected, updateDaySelected } from "./methods/index.js";

const emit = defineEmits(["changeDay"]);
const props = defineProps({
    daySelected: {
        type: String,
        required: true,
    },
    firstDay: {
        type: String,
        default: () => "mon",
    },
    displayDay: {
        type: String,
        default: () => "short",
    },
    disabledDays: {
        type: Array,
        default: () => [],
    },
    variation: {
        type: String,
        default: "standard",
    },
});

const order = ref(["mon", "tue", "wed", "thu", "fri", "sat", "sun"]);
const isDisabled = (day) => (props.disabledDays.indexOf(day) >= 0);
const route = useRoute();

const isSelected = (day) => (props.daySelected === day);

/* eslint-disable object-curly-newline */
const days = shallowRef({
    mon: { short: "mon", abr: "mo", selected: isSelected("mon"), full: "Monday" },
    tue: { short: "tue", abr: "tu", selected: isSelected("tue"), full: "Tuesday" },
    wed: { short: "wed", abr: "we", selected: isSelected("wed"), full: "Wednesday" },
    thu: { short: "thu", abr: "th", selected: isSelected("thu"), full: "Thursday" },
    fri: { short: "fri", abr: "fr", selected: isSelected("fri"), full: "Friday" },
    sat: { short: "sat", abr: "sa", selected: isSelected("sat"), full: "Saturday" },
    sun: { short: "sun", abr: "su", selected: isSelected("sun"), full: "Sunday" },
});
/* eslint-enable object-curly-newline */

const firstDayIndex = order.value.findIndex((day) => day === props.firstDay);

order.value = [
    ...order.value.splice(firstDayIndex),
    ...order.value.slice(0, firstDayIndex),
];

watch(
    () => props.daySelected,
    (day, old) => {
        days.value = changeDaySelected(day, old, days.value);
    },
);

function checkAndUpdateDay() {
    if (isDisabled(props.daySelected)) {
        const availableDayList = order.value.filter((item) => !isDisabled(item));
        if (
            availableDayList.length > 0
            && (route.params.weekday === "" || route.params.weekday === undefined)
        ) {
            updateDaySelected(emit, availableDayList[0]);
        }
    }

    if (
        !isDisabled(props.daySelected)
        && (route.params.weekday === "" || route.params.weekday === undefined)
    ) {
        updateDaySelected(emit, props.daySelected);
    }
}

watch(
    () => props.disabledDays,
    // eslint-disable-next-line no-shadow, no-unused-vars
    (days, old) => {
        checkAndUpdateDay();
    },
);

onMounted(() => {
    checkAndUpdateDay();
});
</script>

<template>
    <div data-test-id="daypicker" data-test="daypicker" class="d-flex daypicker-container" :class="variation">
        <button
            v-for="day in order"
            :key="day"
            :data-test-id="`${day}-button`"
            :data-test="`daypicker-btn-${day}`"
            class="day-btn"
            :class="{
                selected: days[day].selected,
                noevents: isDisabled(day),
            }"
            @click="updateDaySelected(emit, day)"
        >
            {{ days[day].short }}
        </button>
    </div>
</template>

<style scoped>
.day-btn {
    font-family: "Poppins";
    font-style: normal;
    font-size: 0.75rem;
    line-height: 1rem;
    display: flex;
    order: 1;
    flex-direction: column;
    align-items: center;
    flex: 1 1 0;
    margin: 0.4rem;
    text-transform: capitalize;
}

.standard {
    width: 491px;
}

.standard.full-width {
    width: 100%;
}

.standard .day-btn {
    border: 0;
    border-radius: 10rem;
    justify-content: flex-end;
    padding: 0.7rem 0.7rem;
}

.standard .day-btn.noevents {
    color: #999999;
}

.standard .day-btn.selected {
    font-weight: 700;
    color: #FFFFFF;
    background: #FFB700;
}

.standard .day-btn:active, .day-btn:focus {
    border: 0px;
    outline: 0px transparent !important;
}

.round .day-btn {
    border: 1px solid #DDDDDD;
    background-color: white;
    border-radius: 20rem;
    flex-basis: 3rem;
    flex-grow: 0;
    height: 3rem;
    justify-content: center;
    padding: 0;
}

.round .day-btn.selected {
    font-weight: 700;
    color: #FFFFFF;
    background: #FFB700;
}

.round .day-btn:active, .day-btn:focus {
    border: 0px;
    outline: 0px transparent !important;
}

.day-btn.disabled {
    background-color: #EEEEEE;
    color: #999999;
}

@media only screen and (max-width: 768px) {
    .daypicker-container {
        flex-wrap: wrap;
    }
}
</style>
